// Display metadata for verified record facts: badge label, emoji, and a
// type-coded colour. Shared by the structured overview and the timeline.

export interface FactMeta {
  label: string;
  emoji: string;
  color: string;
}

export const FACT_META: Record<string, FactMeta> = {
  commitment: { label: "Commitment", emoji: "🤝", color: "#8b5cf6" }, // purple
  amount: { label: "Amount", emoji: "💰", color: "#16a34a" }, // green
  decision: { label: "Decision", emoji: "⚖️", color: "#2563eb" }, // blue
  action: { label: "Action", emoji: "📋", color: "#6b7280" }, // gray
  request: { label: "Request", emoji: "✉️", color: "#d97706" }, // amber
  deadline: { label: "Deadline", emoji: "⏰", color: "#dc2626" }, // red
  outcome: { label: "Outcome", emoji: "✅", color: "#0d9488" }, // teal
  statement: { label: "Statement", emoji: "💬", color: "#64748b" }, // default
  event: { label: "Event", emoji: "🔴", color: "#f97316" }, // coral
};

export function factMeta(type: string): FactMeta {
  return FACT_META[type] ?? FACT_META.statement!;
}

// Parse a money string ("$1,234.50", "USD 900") to a number; 0 when none.
// SIGNED, conservatively: ONLY a directly-attached leading minus ("-500",
// "−500") parses negative. Parentheses never flip sign — in correspondence
// they're citations, not accounting notation. Mirrors amountTokenSign in
// workers/api/src/lib/validate-ai-output.ts (the validator stores signed
// amount strings under those rules; this parser reads them back the same
// way). When ambiguous: positive.
export function parseAmount(amount: string | null | undefined): number {
  if (!amount) return 0;
  const negative = /^\s*[-−]\s?\$?\s?\d/.test(amount);
  const n = Number.parseFloat(amount.replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? (negative ? -n : n) : 0;
}

// Display title for a fact. AI-generated titles may quote dollar figures;
// a figure is only allowed to render in the title when the fact carries a
// VERIFIED amount (Level 2 validated it against the quote). When the
// verified amount is empty (the validator cleared it — e.g. bare table
// figures like "Total Cost of Repairs 5,751.80" that the deliberately
// narrow quote-scanner doesn't accept), currency figures are stripped from
// the displayed title: the verbatim quote carries the number on verified
// authority. Deterministic, display-only, retroactive to all stored facts —
// no re-titling AI calls.
export function displayFactTitle(f: {
  summary: string;
  amount: string | null;
}): string {
  if (f.amount && f.amount.trim()) return f.summary;
  const stripped = f.summary
    // $-prefixed figures: $X, $X.XX, $X,XXX.XX (incl. "$ 0.50", "$.50")
    .replace(/\$\s?(?:\d[\d,]*(?:\.\d+)?|\.\d+)/g, "")
    // bare comma-grouped decimals: X,XXX.XX (never matches "38 days",
    // dates, or claim numbers — requires the thousands+decimals shape)
    .replace(/\b\d{1,3}(?:,\d{3})+\.\d{2}\b/g, "")
    .replace(/\s{2,}/g, " ")
    .replace(/\s+([,.;:)\]])/g, "$1")
    .replace(/\(\s*\)/g, "")
    .replace(/[\s,;:\-–—]+$/g, "")
    .trim();
  // Never render an empty title; if the title was ONLY a figure, keep it —
  // an odd title beats a blank line.
  return stripped.length >= 3 ? stripped : f.summary;
}

// Source line for a fact card. When the fact's event date differs from its
// source record's date (a record RECOUNTING an earlier event — "theft on
// March 15" inside an April 22 demand letter), the label makes the
// relationship explicit: 'mentioned in <Record Title> (<record date>)'.
// Same-date facts keep the plain record title. Display-only; both dates
// already exist on the fact/record.
export function factSourceLabel(
  fact: { date: string | null },
  record?: {
    subject: string | null;
    record_date: string;
    source_type?: string;
    from_addr?: string | null;
  } | null,
): { text: string; dateContext: boolean } {
  const title =
    record?.subject?.trim() ||
    (record
      ? `${record.source_type ?? "record"} — ${record.from_addr ?? "?"}`
      : "source record");
  const factDate = fact.date?.slice(0, 10);
  const recDate = record?.record_date?.slice(0, 10);
  if (factDate && recDate && factDate !== recDate) {
    return {
      text: `mentioned in ${title} (${formatFactDate(recDate)})`,
      dateContext: true,
    };
  }
  return { text: title, dateContext: false };
}

// "2026-05-27" → "May 27, 2026"; null/invalid → "Undated".
export function formatFactDate(d: string | null | undefined): string {
  if (!d) return "Undated";
  const dt = new Date(`${d}T00:00:00`);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}
